import { Directive, ElementRef, Renderer2, inject } from '@angular/core';

const PARALLAX_VALUE = 0.6;

interface ParallaxItem {
  element: HTMLElement;
  value: number;
  depth: number;
  scale: number;
  isMainElement?: boolean; // if using more than one parallaxing elements (currently not supported)
}

@Directive({
  selector: '[proto-parallax]',
  standalone: false,
})
export class ProtoParallaxDirective {
  private parallaxContainer: HTMLElement;
  private parallaxHeader: ParallaxItem;
  private isInitialized: boolean = false;

  private element = inject(ElementRef);
  private renderer = inject(Renderer2);

  public ngAfterViewInit(): void {
    const parallaxSupported = document.body.classList.contains('webkit');

    if (!parallaxSupported) { return; }

    this.parallaxContainer = this.element.nativeElement.querySelector('.proto-content');
    if (!this.parallaxContainer) { return; }

    const parallaxElement: HTMLElement = this.parallaxContainer.querySelector('.proto-content__header');

    if (!parallaxElement) { return; }

    this.parallaxHeader = this.getParallaxItem(parallaxElement);
  }

  public ngAfterViewChecked(): void {
    if (!this.parallaxContainer || !this.parallaxHeader) { return; }
    const mainElementHeight = this.parallaxHeader.element.offsetHeight;

    // wait until parallaxHeader height is defined
    if (this.isInitialized || !mainElementHeight) { return; }

    this.isInitialized = true;
    this.initParallax();
  }

  private initParallax(): void {
    this.renderer.setStyle(this.parallaxHeader.element,
      'transform',
      `translateZ(${this.parallaxHeader.depth}px) scale(${this.parallaxHeader.scale})`);
  }

  private getParallaxItem(element: HTMLElement): ParallaxItem {
    // detault value ("perspective: 1px;" in related css)
    const perspective = 1.0;

    // parallaxValue <= 0 or rate == perspective : no parallax
    // 0 < parallaxValue < perspective : moves slower than scroll container
    // perspective < parallaxValue : moves faster than scroll container
    const parallaxValue = PARALLAX_VALUE;

    return {
      get depth(): number {
        // eslint-disable-next-line no-mixed-operators
        return 1 - 1 / this.value;
      },
      element,
      get scale(): number {
        return (perspective - this.depth) / perspective;
      },
      get value(): number {
        return PARALLAX_VALUE > 0
          ? parallaxValue
          : perspective;
      },
    };
  }
}
